import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import { barriosPorDepartamento } from '../../data/barriosMendoza';
import { apiUrl } from '../../config/api';

const PROVINCES = [
  { id: 'mendoza', name: 'Mendoza', file: '/geo/mendoza-departamentos.geojson' },
  { id: 'san_juan', name: 'San Juan', file: '/geo/departamentos-san_juan.geojson' },
  { id: 'san_luis', name: 'San Luis', file: '/geo/departamentos-san_luis.geojson' },
];

const WIDTH = 760;
const HEIGHT = 650;
const PADDING = 22;

const normalize = (value = '') => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_|_$/g, '');

const formatCount = (value) => new Intl.NumberFormat('es-AR').format(Number(value) || 0);
const LOWERCASE_NAME_WORDS = new Set(['a', 'al', 'de', 'del', 'e', 'el', 'la', 'las', 'los', 'o', 'y']);
const titleCase = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase('es-AR')
  .split(/\s+/)
  .map((word, index) => {
    if (index > 0 && LOWERCASE_NAME_WORDS.has(word)) return word;
    return word.replace(/^\p{L}/u, (letter) => letter.toLocaleUpperCase('es-AR'));
  })
  .join(' ');

function countsFromProperties(payload) {
  const properties = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
  const provinceTotals = new Map();
  const departmentTotals = new Map();
  const neighborhoodTotals = new Map();

  properties.forEach((property) => {
    if (property.status && property.status !== 'disponible') return;
    const province = normalize(property.province || 'mendoza') || 'mendoza';
    const department = property.department || 'sin_departamento';
    const neighborhood = property.neighborhood || 'sin_barrio';
    provinceTotals.set(province, (provinceTotals.get(province) || 0) + 1);
    const departmentKey = `${province}::${department}`;
    departmentTotals.set(departmentKey, (departmentTotals.get(departmentKey) || 0) + 1);
    const neighborhoodKey = `${province}::${department}::${neighborhood}`;
    neighborhoodTotals.set(neighborhoodKey, (neighborhoodTotals.get(neighborhoodKey) || 0) + 1);
  });

  return {
    total: [...provinceTotals.values()].reduce((sum, value) => sum + value, 0),
    provinces: [...provinceTotals].map(([_id, total]) => ({ _id, total })),
    departments: [...departmentTotals].map(([key, total]) => {
      const [province, department] = key.split('::');
      return { _id: { province, department }, total };
    }),
    neighborhoods: [...neighborhoodTotals].map(([key, total]) => {
      const [province, department, neighborhood] = key.split('::');
      return { _id: { province, department, neighborhood }, total };
    }),
  };
}

async function loadTerritoryCounts() {
  try {
    const operations = ['venta', 'alquiler', 'alquiler_temporal'];
    const responses = await Promise.all(operations.map((operation) => (
      fetch(apiUrl(`/properties/location-counts?operation=${operation}`))
    )));
    if (responses.some((response) => !response.ok)) throw new Error('Respuesta territorial inválida');
    const datasets = await Promise.all(responses.map((response) => response.json()));
    const merge = (key) => {
      const totals = new Map();
      datasets.flatMap((data) => data[key] || []).forEach((item) => {
        const id = JSON.stringify(item._id);
        const current = totals.get(id) || { _id:item._id, total:0 };
        current.total += Number(item.total || 0);
        totals.set(id, current);
      });
      return [...totals.values()];
    };
    return {
      total: datasets.reduce((sum, data) => sum + Number(data.total || 0), 0),
      provinces: merge('provinces'),
      departments: merge('departments'),
      neighborhoods: merge('neighborhoods'),
    };
  } catch {
    const response = await fetch(apiUrl('/properties?limit=500'));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    const properties = (Array.isArray(payload) ? payload : payload?.data || [])
      .filter((property) => property.operation !== 'alquiler_vacacional');
    return countsFromProperties(properties);
  }
}

function coordinatePairs(coordinates, result = []) {
  if (!Array.isArray(coordinates)) return result;
  if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') result.push(coordinates);
  else coordinates.forEach((item) => coordinatePairs(item, result));
  return result;
}

function createProjection(features, extraPoints = []) {
  const points = features.flatMap((feature) => coordinatePairs(feature.geometry?.coordinates)).concat(extraPoints);
  if (!points.length) return (point) => point;
  const longitudes = points.map(([longitude]) => longitude);
  const latitudes = points.map(([, latitude]) => latitude);
  const minLongitude = Math.min(...longitudes);
  const maxLongitude = Math.max(...longitudes);
  const minLatitude = Math.min(...latitudes);
  const maxLatitude = Math.max(...latitudes);
  const spanLongitude = Math.max(maxLongitude - minLongitude, .01);
  const spanLatitude = Math.max(maxLatitude - minLatitude, .01);
  const scale = Math.min((WIDTH - PADDING * 2) / spanLongitude, (HEIGHT - PADDING * 2) / spanLatitude);
  const offsetX = (WIDTH - spanLongitude * scale) / 2;
  const offsetY = (HEIGHT - spanLatitude * scale) / 2;
  return ([longitude, latitude]) => [
    offsetX + (longitude - minLongitude) * scale,
    offsetY + (maxLatitude - latitude) * scale,
  ];
}

function ringPath(ring, project) {
  return ring.map((point, index) => {
    const [x, y] = project(point);
    return `${index ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ') + ' Z';
}

function featurePath(feature, project) {
  const { type, coordinates } = feature.geometry || {};
  if (type === 'Polygon') return coordinates.map((ring) => ringPath(ring, project)).join(' ');
  if (type === 'MultiPolygon') return coordinates.flatMap((polygon) => polygon.map((ring) => ringPath(ring, project))).join(' ');
  return '';
}

function featureCenter(feature, project) {
  const points = coordinatePairs(feature.geometry?.coordinates);
  if (!points.length) return [0, 0];
  const center = points.reduce(([longitude, latitude], point) => [longitude + point[0], latitude + point[1]], [0, 0]);
  return project([center[0] / points.length, center[1] / points.length]);
}

function provinceCenter(features, project) {
  const points = features.flatMap((feature) => coordinatePairs(feature.geometry?.coordinates));
  if (!points.length) return [0, 0];
  const center = points.reduce(([longitude, latitude], point) => [longitude + point[0], latitude + point[1]], [0, 0]);
  return project([center[0] / points.length, center[1] / points.length]);
}

export default function CuyoTerritoryExplorer() {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState({});
  const [counts, setCounts] = useState({ total: 0, provinces: [], departments: [], neighborhoods: [] });
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(true);
  const [officialZones, setOfficialZones] = useState({});
  const [loadingZones, setLoadingZones] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all(PROVINCES.map(async (province) => [province.id, await fetch(province.file).then((response) => response.json())]))
    .then((results) => {
      if (!active) return;
      setGeoData(Object.fromEntries(results));
    }).catch(() => {}).finally(() => active && setLoading(false));

    // Los conteos pueden depender de un backend con cold start. Se cargan en
    // paralelo, pero nunca bloquean la primera pintura del mapa.
    loadTerritoryCounts()
      .then((territoryCounts) => active && setCounts(territoryCounts || {}))
      .catch(() => {});

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedProvince || !selectedDepartment) return undefined;
    const key = `${selectedProvince.id}::${normalize(selectedDepartment.name)}`;
    if (officialZones[key]) return undefined;

    const controller = new AbortController();
    const params = new URLSearchParams({
      provincia: selectedProvince.name,
      departamento: selectedDepartment.name,
      max: '1000',
      campos: 'id,nombre,centroide',
    });
    setLoadingZones(true);
    fetch(`https://apis.datos.gob.ar/georef/api/localidades?${params.toString()}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const zones = (payload.localidades || []).map((locality) => ({
          id: normalize(locality.id || locality.nombre),
          name: titleCase(locality.nombre),
          center: locality.centroide ? { lat: locality.centroide.lat, lng: locality.centroide.lon } : null,
          source: 'georef',
        }));
        setOfficialZones((current) => ({ ...current, [key]: zones }));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setOfficialZones((current) => ({ ...current, [key]: [] }));
      })
      .finally(() => setLoadingZones(false));
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince, selectedDepartment]);

  const provinceCount = (provinceId) => (counts.provinces || []).reduce((total, item) => (
    normalize(item._id) === provinceId ? total + Number(item.total || 0) : total
  ), 0);
  const departmentCount = (provinceId, department) => (counts.departments || []).reduce((total, item) => (
    normalize(item._id?.province) === provinceId && normalize(item._id?.department) === normalize(department)
      ? total + Number(item.total || 0) : total
  ), 0);
  const neighborhoodCount = (provinceId, department, neighborhood) => (counts.neighborhoods || []).reduce((total, item) => (
    normalize(item._id?.province) === provinceId
    && normalize(item._id?.department) === normalize(department)
    && normalize(item._id?.neighborhood) === normalize(neighborhood)
      ? total + Number(item.total || 0) : total
  ), 0);
  const departmentDisplayName = (provinceId, department) => (
    (counts.departments || []).find((item) => (
      normalize(item._id?.province) === provinceId
      && normalize(item._id?.department) === normalize(department)
    ))?._id?.department || titleCase(department)
  );

  const stage = selectedDepartment ? 'neighborhood' : selectedProvince ? 'department' : 'province';
  const selectedGeo = selectedProvince ? geoData[selectedProvince.id] : null;
  const departmentFeatures = selectedGeo?.features || [];
  const currentDepartmentFeature = selectedDepartment
    ? departmentFeatures.find((feature) => normalize(feature.properties?.departamento) === normalize(selectedDepartment.name))
    : null;

  const neighborhoodItems = useMemo(() => {
    if (!selectedProvince || !selectedDepartment) return [];
    const territoryKey = `${selectedProvince.id}::${normalize(selectedDepartment.name)}`;
    const curated = selectedProvince.id === 'mendoza'
      ? (barriosPorDepartamento[normalize(selectedDepartment.name)] || [])
      : [];
    const official = officialZones[territoryKey] || [];
    const countedByName = new Map();
    (counts.neighborhoods || [])
      .filter((item) => normalize(item._id?.province) === selectedProvince.id
        && normalize(item._id?.department) === normalize(selectedDepartment.name)
        && normalize(item._id?.neighborhood) !== 'sin_barrio')
      .forEach((item) => {
        const id = normalize(item._id.neighborhood);
        const previous = countedByName.get(id);
        countedByName.set(id, {
          id,
          name: previous?.name || item._id.neighborhood,
          count: Number(previous?.count || 0) + Number(item.total || 0),
        });
      });
    const counted = [...countedByName.values()];
    const combined = new Map();
    official.forEach((item) => {
      const id = normalize(item.name);
      if (!combined.has(id)) combined.set(id, {
        ...item,
        id,
        count: neighborhoodCount(selectedProvince.id, selectedDepartment.name, item.name),
      });
    });
    curated.forEach((item) => {
      const id = normalize(item.name);
      const existing = combined.get(id);
      combined.set(id, {
        ...existing,
        ...item,
        id,
        count: neighborhoodCount(selectedProvince.id, selectedDepartment.name, item.name),
      });
    });
    counted.forEach((item) => {
      if (!combined.has(item.id)) combined.set(item.id, item);
      else combined.set(item.id, { ...combined.get(item.id), name: item.name, count: item.count });
    });
    return [...combined.values()];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince, selectedDepartment, counts, officialZones]);

  const mapFeatures = stage === 'province'
    ? PROVINCES.flatMap((province) => (geoData[province.id]?.features || []).map((feature) => ({ ...feature, province })))
    : stage === 'department' ? departmentFeatures : currentDepartmentFeature ? [currentDepartmentFeature] : [];
  const pointCoordinates = neighborhoodItems.filter((item) => item.center).map((item) => [item.center.lng, item.center.lat]);
  const project = createProjection(mapFeatures, pointCoordinates);

  const selectProvince = (province) => {
    setSelectedProvince(province); setSelectedDepartment(null); setSelectedNeighborhood(null); setHovered(null);
  };
  const selectDepartment = (name) => {
    const displayName = departmentDisplayName(selectedProvince.id, name);
    setSelectedDepartment({ id: normalize(name), name: displayName }); setSelectedNeighborhood(null); setHovered(null);
  };
  const goBack = () => {
    if (selectedNeighborhood) setSelectedNeighborhood(null);
    else if (selectedDepartment) setSelectedDepartment(null);
    else setSelectedProvince(null);
  };
  const activateWithKeyboard = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };
  const openTerritoryResults = ({
    province = selectedProvince,
    department = selectedDepartment,
    neighborhood = selectedNeighborhood,
  } = {}) => {
    const params = new URLSearchParams();
    if (province) params.set('province', province.id);
    if (department) params.set('department', department.name || department);
    if (neighborhood) params.set('neighborhood', neighborhood.name || neighborhood);
    const visibleZone = neighborhood
      ? (neighborhood.name || neighborhood)
      : department ? (department.name || department) : '';
    if (visibleZone) params.set('location', visibleZone);
    navigate(`/buscar${params.size ? `?${params.toString()}` : ''}`);
  };
  const openResults = () => openTerritoryResults();

  const handleListSelection = (item) => {
    if (stage === 'province') {
      selectProvince(item);
      return;
    }
    if (stage === 'department') {
      selectDepartment(item.name);
      return;
    }
    openTerritoryResults({
      province: selectedProvince,
      department: selectedDepartment,
      neighborhood: item,
    });
  };

  const stageTitle = selectedNeighborhood
    ? selectedNeighborhood.name
    : selectedDepartment ? selectedDepartment.name
      : selectedProvince ? selectedProvince.name : 'Cuyo';
  const stageCount = selectedNeighborhood
    ? neighborhoodCount(selectedProvince.id, selectedDepartment.name, selectedNeighborhood.name)
    : selectedDepartment ? departmentCount(selectedProvince.id, selectedDepartment.name)
      : selectedProvince ? provinceCount(selectedProvince.id) : counts.total;

  const listItems = stage === 'province'
    ? PROVINCES.map((province) => ({ ...province, count: provinceCount(province.id) }))
    : stage === 'department'
      ? departmentFeatures.map((feature) => {
        const name = feature.properties?.departamento;
        return { id: normalize(name), name: departmentDisplayName(selectedProvince.id, name), count: departmentCount(selectedProvince.id, name) };
      }).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      : neighborhoodItems.sort((a, b) => (b.count || 0) - (a.count || 0));
  const hoveredItem = listItems.find((item) => normalize(item.id || item.name) === hovered);

  return (
    <Explorer>
      <ExplorerHeader>
        <div>
          <Kicker>EXPLORÁ POR UBICACIÓN</Kicker>
          <Title>Explorá Cuyo a tu ritmo.</Title>
          <Intro>Recorré provincias, departamentos, barrios y localidades. El listado se actualiza en cada selección.</Intro>
        </div>
        <StageCount><span>{stageTitle}</span><strong>{formatCount(stageCount)}</strong><small>propiedades</small></StageCount>
      </ExplorerHeader>

      <Breadcrumbs>
        <Crumb type="button" $active={!selectedProvince} onClick={() => { setSelectedProvince(null); setSelectedDepartment(null); setSelectedNeighborhood(null); }}>Cuyo</Crumb>
        {selectedProvince && <><i /> <Crumb type="button" $active={!selectedDepartment} onClick={() => { setSelectedDepartment(null); setSelectedNeighborhood(null); }}>{selectedProvince.name}</Crumb></>}
        {selectedDepartment && <><i /> <Crumb type="button" $active={!selectedNeighborhood} onClick={() => setSelectedNeighborhood(null)}>{selectedDepartment.name}</Crumb></>}
        {selectedNeighborhood && <><i /> <Crumb type="button" $active>{selectedNeighborhood.name}</Crumb></>}
      </Breadcrumbs>

      <ExplorerLayout>
        <MapPanel>
          <MapGuide>
            <small>{stage === 'province' ? 'REGIÓN' : stage === 'department' ? 'PROVINCIA' : 'DEPARTAMENTO'}</small>
            <strong>{hoveredItem?.name || stageTitle}</strong>
            <span>{hoveredItem ? `${formatCount(hoveredItem.count)} propiedades` : stage === 'province' ? 'Elegí una provincia' : stage === 'department' ? 'Elegí un departamento' : 'Elegí una zona'}</span>
          </MapGuide>
          {loading ? <MapLoading>Preparando el territorio…</MapLoading> : (
            <Svg viewBox={stage === 'province' ? `70 0 620 ${HEIGHT}` : `0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`Mapa interactivo de ${stageTitle}`}>
              <defs>
                <linearGradient id="territory-idle" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#eef0eb" />
                  <stop offset="100%" stopColor="#dfe5df" />
                </linearGradient>
                <linearGradient id="territory-active" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#e7ad94" />
                  <stop offset="100%" stopColor="#cf8265" />
                </linearGradient>
                <filter id="map-soft-shadow" x="-30%" y="-30%" width="160%" height="170%">
                  <feDropShadow dx="0" dy="15" stdDeviation="14" floodColor="#29474f" floodOpacity=".13" />
                </filter>
              </defs>
              <circle cx="380" cy="322" r="248" fill="none" stroke="#29474f" strokeOpacity=".035" />
              <circle cx="380" cy="322" r="205" fill="none" stroke="#b45f40" strokeOpacity=".035" />
              {stage === 'province' && PROVINCES.map((province) => {
                const active = hovered === province.id;
                return (
                  <g key={province.id} onMouseEnter={() => setHovered(province.id)} onMouseLeave={() => setHovered(null)} onClick={() => selectProvince(province)} onKeyDown={(event) => activateWithKeyboard(event, () => selectProvince(province))} role="button" tabIndex="0" aria-label={`Explorar ${province.name}`}>
                    {(geoData[province.id]?.features || []).map((feature, index) => <ProvincePath key={index} d={featurePath(feature, project)} $active={active} />)}
                  </g>
                );
              })}
              {stage === 'province' && PROVINCES.map((province) => {
                const features = geoData[province.id]?.features || [];
                if (!features.length) return null;
                const active = hovered === province.id;
                const [x, y] = provinceCenter(features, project);
                return <g key={`${province.id}-label`} pointerEvents="none">
                  <MapLabelCard x={x - 61} y={y - 31} width="122" height="62" rx="18" $active={active} />
                  <MapText x={x} y={y - 5} $active={active}><tspan x={x}>{province.name}</tspan><tspan x={x} dy="20">{formatCount(provinceCount(province.id))} propiedades</tspan></MapText>
                </g>;
              })}

              {stage === 'department' && departmentFeatures.map((feature) => {
                const name = feature.properties?.departamento;
                const id = normalize(name);
                const active = hovered === id;
                return (
                  <g key={id} onMouseEnter={() => setHovered(id)} onMouseLeave={() => setHovered(null)} onClick={() => selectDepartment(name)} onKeyDown={(event) => activateWithKeyboard(event, () => selectDepartment(name))} role="button" tabIndex="0" aria-label={`Explorar ${titleCase(name)}`}>
                    <DepartmentPath d={featurePath(feature, project)} $active={active} />
                  </g>
                );
              })}
              {stage === 'department' && hoveredItem && (() => {
                const feature = departmentFeatures.find((item) => normalize(item.properties?.departamento) === hoveredItem.id);
                if (!feature) return null;
                const [x, y] = featureCenter(feature, project);
                const mapLabel = hoveredItem.name.length > 24 ? `${hoveredItem.name.slice(0, 23)}…` : hoveredItem.name;
                return <g pointerEvents="none">
                  <DepartmentLabelCard x={x - 92} y={y - 29} width="184" height="58" rx="17" />
                  <DepartmentText x={x} y={y - 3}><tspan x={x}>{mapLabel}</tspan><tspan x={x} dy="18">{formatCount(hoveredItem.count)} propiedades</tspan></DepartmentText>
                </g>;
              })()}

              {stage === 'neighborhood' && currentDepartmentFeature && (
                <DepartmentPath d={featurePath(currentDepartmentFeature, project)} $muted />
              )}
              {stage === 'neighborhood' && neighborhoodItems.filter((item) => item.center && item.count > 0).map((item) => {
                const [x, y] = project([item.center.lng, item.center.lat]);
                const active = hovered === normalize(item.id || item.name) || selectedNeighborhood?.id === item.id;
                const openNeighborhood = () => openTerritoryResults({
                  province: selectedProvince,
                  department: selectedDepartment,
                  neighborhood: item,
                });
                return (
                  <g key={item.id || item.name} onMouseEnter={() => setHovered(normalize(item.id || item.name))} onMouseLeave={() => setHovered(null)} onClick={openNeighborhood} onKeyDown={(event) => activateWithKeyboard(event, openNeighborhood)} role="button" tabIndex="0" aria-label={`Ver propiedades en ${item.name}`}>
                    <PointHalo cx={x} cy={y} r={active ? 25 : 19} $active={active} />
                    <Point cx={x} cy={y} r={active ? 10 : 8} $active={active} />
                    <PointLabel x={x} y={y - 28}>{item.name}</PointLabel>
                  </g>
                );
              })}
            </Svg>
          )}
          {selectedProvince && <BackButton type="button" onClick={goBack}><FiArrowLeft /> Volver</BackButton>}
          {hovered && <HoverHint>{stage === 'neighborhood' ? 'Ver propiedades en esta zona' : 'Hacé clic para explorar'}</HoverHint>}
        </MapPanel>

        <TerritoryList>
          <ListHeader><span>{stage === 'province' ? 'Provincias' : stage === 'department' ? 'Departamentos' : 'Barrios y localidades'}</span><small>{loadingZones && stage === 'neighborhood' ? 'Buscando…' : `${listItems.length} zonas`}</small></ListHeader>
          <ListContext>
            {stage === 'province' && 'Elegí una provincia para conocer sus departamentos.'}
            {stage === 'department' && `Elegí un departamento de ${selectedProvince?.name}.`}
            {stage === 'neighborhood' && `Afiná por zona o mirá todas las propiedades de ${selectedDepartment?.name}.`}
          </ListContext>
          {(stage === 'department' || stage === 'neighborhood') && (
            <ListAllButton type="button" onClick={openResults}>
              <span>
                <small>{stage === 'department' ? 'VER TODA LA PROVINCIA' : 'VER TODO EL DEPARTAMENTO'}</small>
                <strong>{stage === 'department' ? selectedProvince?.name : selectedDepartment?.name}</strong>
              </span>
              <FiArrowUpRight />
            </ListAllButton>
          )}
          <ListScroll>
            {loadingZones && stage === 'neighborhood' ? <ZoneLoading><i /><span>Buscando zonas de {selectedDepartment?.name}…</span></ZoneLoading> : listItems.length ? listItems.map((item) => {
              const id = item.id || normalize(item.name);
              return (
                <TerritoryButton
                  type="button" key={id} $active={hovered === id || selectedNeighborhood?.id === item.id}
                  onMouseEnter={() => setHovered(id)} onMouseLeave={() => setHovered(null)}
                  onClick={() => handleListSelection(item)}
                  aria-label={stage === 'province' ? `Explorar ${item.name}` : `Ver propiedades en ${item.name}`}
                >
                  <MapDot /><span><strong>{item.name}</strong><small>{formatCount(item.count)} propiedades</small></span>{stage === 'neighborhood' ? <FiArrowUpRight /> : <FiArrowRight />}
                </TerritoryButton>
              );
            }) : (
              <EmptyList>
                <EmptyMark>→</EmptyMark>
                <strong>Este departamento no tiene barrios o localidades disponibles para explorar.</strong>
                <span>Usá “Ver todo el departamento” para conocer las propiedades publicadas en {selectedDepartment?.name}.</span>
              </EmptyList>
            )}
          </ListScroll>
        </TerritoryList>
      </ExplorerLayout>
      <ContinuePanel>
        <div>
          <Kicker>SELECCIÓN TERRITORIAL</Kicker>
          <ContinueTitle>{stageTitle}</ContinueTitle>
          <ContinueCopy>{formatCount(stageCount)} propiedades disponibles para seguir explorando.</ContinueCopy>
        </div>
        <ContinueButton type="button" onClick={openResults}>
          Ver propiedades <FiArrowUpRight />
        </ContinueButton>
      </ContinuePanel>
    </Explorer>
  );
}

const Explorer = styled.section`max-width:1440px;margin:0 auto;padding:clamp(58px,8vw,112px) clamp(16px,4vw,58px) 120px;color:#29474f;font-family:'Space Grotesk',system-ui,sans-serif;@media(max-width:600px){padding-bottom:76px;}`;
const ExplorerHeader = styled.div`display:flex;align-items:flex-end;justify-content:space-between;gap:40px;margin-bottom:35px;@media(max-width:760px){align-items:flex-start;flex-direction:column;gap:24px;}`;
const Kicker = styled.div`display:flex;align-items:center;gap:9px;margin-bottom:13px;color:#b45f40;font-size:12px;font-weight:700;letter-spacing:.18em;&::before{content:'';width:23px;height:1px;background:currentColor;}`;
const Title = styled.h1`max-width:850px;margin:0 0 15px;color:#20373c;font-size:clamp(40px,6vw,82px);font-weight:520;line-height:.96;letter-spacing:-.06em;`;
const Intro = styled.p`max-width:660px;margin:0;color:#68777b;font-size:clamp(15px,1.4vw,18px);line-height:1.65;`;
const StageCount = styled.div`min-width:205px;padding:19px 22px;background:#fcfbf8;border:1px solid rgba(42,64,70,.13);box-shadow:0 12px 38px rgba(41,71,79,.06);span,strong,small{display:block;}span{color:#b45f40;font-size:12px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;}strong{margin:5px 0 2px;color:#20373c;font-size:48px;font-weight:520;line-height:1;letter-spacing:-.05em;}small{color:#7c898b;font-size:12px;}@media(max-width:760px){min-width:170px;padding:15px 18px;strong{font-size:40px;}}`;
const Breadcrumbs = styled.nav`display:flex;align-items:center;gap:10px;margin-bottom:16px;overflow-x:auto;scrollbar-width:none;i{width:5px;height:5px;flex:0 0 auto;background:#aeb9b7;border-radius:50%;}`;
const Crumb = styled.button`min-height:42px;padding:10px 16px;color:${({$active})=>$active?'#fff':'#52666a'};background:${({$active})=>$active?'#29474f':'rgba(255,255,255,.66)'};border:1px solid ${({$active})=>$active?'#29474f':'rgba(41,71,79,.13)'};border-radius:999px;font:inherit;font-size:14px;font-weight:650;line-height:1;white-space:nowrap;cursor:pointer;transition:.2s;&:hover{color:${({$active})=>$active?'#fff':'#a95336'};border-color:rgba(169,83,54,.36);transform:translateY(-1px);}@media(max-width:600px){min-height:44px;padding:11px 17px;font-size:15px;}`;
const ExplorerLayout = styled.div`display:grid;grid-template-columns:minmax(0,1.55fr) minmax(300px,.55fr);min-height:650px;background:#fbfaf7;border:1px solid rgba(42,64,70,.12);border-radius:18px;box-shadow:0 34px 90px rgba(42,64,70,.08);overflow:hidden;@media(max-width:850px){min-height:0;grid-template-columns:1fr;}@media(max-width:600px){border-radius:14px;}`;
const MapPanel = styled.div`position:relative;min-width:0;min-height:650px;background:transparent;border-right:1px solid rgba(42,64,70,.09);isolation:isolate;@media(max-width:850px){min-height:auto;border-right:0;border-bottom:1px solid rgba(42,64,70,.09);}@media(max-width:600px){display:none;}`;
const MapGuide = styled.div`
  position:absolute;z-index:2;top:22px;right:22px;display:grid;min-width:175px;gap:2px;padding:13px 16px;
  color:#29474f;background:rgba(255,255,255,.82);border:1px solid rgba(42,64,70,.09);border-radius:14px;
  box-shadow:0 12px 34px rgba(42,64,70,.08);backdrop-filter:blur(14px);pointer-events:none;
  small{color:#a95336;font-size:12px;font-weight:780;letter-spacing:.14em;}
  strong{font-size:20px;font-weight:680;letter-spacing:-.025em;}
  span{color:#627376;font-size:13px;font-weight:520;}
  @media(max-width:600px){top:14px;right:14px;min-width:148px;padding:11px 13px;strong{font-size:15px;}span{font-size:12px;}}
`;
const Svg = styled.svg`display:block;width:100%;height:650px;touch-action:manipulation;g{cursor:pointer;outline:none;}g:focus-visible path{stroke:#29474f;stroke-width:3px;}`;
const ProvincePath = styled.path`
  fill:${({$active})=>$active?'url(#territory-active)':'url(#territory-idle)'};
  stroke:${({$active})=>$active?'rgba(185,109,80,.58)':'rgba(255,255,255,.96)'};
  stroke-width:${({$active})=>$active?1.7:1.35};
  filter:${({$active})=>$active?'url(#map-soft-shadow)':'none'};
  transform-box:fill-box;
  transform-origin:center;
  transform:${({$active})=>$active?'translateY(-3px) scale(1.012)':'none'};
  transition:fill .32s ease,stroke .32s ease,filter .32s ease,transform .32s cubic-bezier(.2,.8,.2,1);
`;
const DepartmentPath = styled.path`
  fill:${({$active,$muted})=>$active?'url(#territory-active)':$muted?'#e7eae5':'url(#territory-idle)'};
  stroke:${({$active})=>$active?'rgba(185,109,80,.62)':'rgba(255,255,255,.96)'};
  stroke-width:${({$active})=>$active?2:1.4};
  filter:${({$active})=>$active?'url(#map-soft-shadow)':'none'};
  transform-box:fill-box;
  transform-origin:center;
  transform:${({$active})=>$active?'translateY(-2px) scale(1.015)':'none'};
  transition:fill .28s ease,stroke .28s ease,filter .28s ease,transform .28s cubic-bezier(.2,.8,.2,1);
`;
const MapLabelCard = styled.rect`pointer-events:none;fill:${({$active})=>$active?'rgba(41,71,79,.94)':'rgba(255,255,255,.9)'};stroke:${({$active})=>$active?'rgba(41,71,79,.08)':'rgba(41,71,79,.09)'};stroke-width:1;filter:drop-shadow(0 7px 14px rgba(41,71,79,.1));transition:.28s ease;`;
const MapText = styled.text`pointer-events:none;fill:${({$active})=>$active?'#fff':'#29474f'};font-size:16px;font-weight:720;text-anchor:middle;letter-spacing:-.02em;transition:fill .28s ease;tspan:last-child{fill:${({$active})=>$active?'rgba(255,255,255,.82)':'#627376'};font-size:12px;font-weight:650;letter-spacing:.01em;}`;
const DepartmentLabelCard = styled.rect`pointer-events:none;fill:rgba(41,71,79,.95);stroke:rgba(255,255,255,.16);stroke-width:1;filter:drop-shadow(0 10px 18px rgba(41,71,79,.18));`;
const DepartmentText = styled.text`pointer-events:none;fill:#fff;font-size:15px;font-weight:730;text-anchor:middle;letter-spacing:-.015em;tspan:last-child{fill:rgba(255,255,255,.78);font-size:12px;font-weight:620;letter-spacing:.01em;}`;
const PointHalo = styled.circle`fill:${({$active})=>$active?'rgba(180,95,64,.22)':'rgba(41,71,79,.14)'};transition:.2s;`;
const Point = styled.circle`fill:${({$active})=>$active?'#b45f40':'#29474f'};stroke:#fff;stroke-width:3;transition:.2s;`;
const PointLabel = styled.text`pointer-events:none;fill:#29474f;font-size:13px;font-weight:720;text-anchor:middle;paint-order:stroke;stroke:#fff;stroke-width:5px;`;
const MapLoading = styled.div`display:grid;height:650px;place-items:center;color:#5d6f73;font-size:15px;font-weight:550;@media(max-width:600px){height:470px;}`;
const BackButton = styled.button`position:absolute;top:20px;left:20px;display:flex;min-height:44px;align-items:center;gap:8px;padding:0 16px;color:#29474f;background:rgba(255,255,255,.82);border:1px solid rgba(42,64,70,.1);border-radius:999px;box-shadow:0 10px 30px rgba(42,64,70,.08);backdrop-filter:blur(14px);font-weight:700;cursor:pointer;transition:.2s;&:hover{background:#fff;transform:translateY(-1px);}`;
const HoverHint = styled.div`position:absolute;right:20px;bottom:20px;padding:13px 18px;color:#fff;background:rgba(41,71,79,.94);border:1px solid rgba(255,255,255,.14);border-radius:999px;box-shadow:0 12px 30px rgba(41,71,79,.18);backdrop-filter:blur(12px);font-size:14px;font-weight:620;letter-spacing:0;pointer-events:none;`;
const TerritoryList = styled.aside`display:flex;min-width:0;max-height:650px;flex-direction:column;background:#fcfbf8;@media(max-width:850px){max-height:460px;}@media(max-width:600px){max-height:none;min-height:390px;}`;
const ListHeader = styled.div`display:flex;align-items:center;justify-content:space-between;padding:27px 24px;border-bottom:1px solid rgba(42,64,70,.1);span{font-size:22px;font-weight:620;letter-spacing:-.025em;}small{color:#6f7e80;font-size:12px;font-weight:620;letter-spacing:.07em;text-transform:uppercase;}@media(max-width:600px){padding:24px 20px 19px;span{font-size:25px;font-weight:620;}small{font-size:12px;}}`;
const ListContext = styled.p`margin:0;padding:17px 24px 14px;color:#627376;font-size:14px;font-weight:480;line-height:1.55;@media(max-width:600px){padding:16px 20px 13px;font-size:15px;}`;
const ListAllButton = styled.button`display:flex;min-height:72px;align-items:center;justify-content:space-between;gap:15px;margin:0 15px 8px;padding:13px 16px;color:#fff;background:#29474f;border:0;border-radius:12px;font:inherit;text-align:left;cursor:pointer;transition:.2s;span small,span strong{display:block;}small{margin-bottom:5px;color:rgba(255,255,255,.74);font-size:12px;font-weight:720;letter-spacing:.1em;}strong{font-size:16px;font-weight:630;}svg{font-size:19px;}&:hover{background:#b45f40;transform:translateY(-1px);}`;
const ListScroll = styled.div`overflow-y:auto;padding:5px 15px 18px;scrollbar-width:thin;scrollbar-color:#c7d0cd transparent;@media(max-width:600px){max-height:520px;padding:4px 12px 16px;}`;
const TerritoryButton = styled.button`display:grid;width:100%;grid-template-columns:auto 1fr auto;align-items:center;gap:14px;padding:18px 4px;color:#29474f;background:transparent;border:0;border-bottom:1px solid rgba(42,64,70,.09);border-radius:0;font:inherit;text-align:left;cursor:pointer;transition:.2s;&:hover{padding-left:9px;color:#a95336;}&[data-active='true']{color:#a95336;}span strong,span small{display:block;}strong{margin-bottom:5px;font-size:17px;font-weight:630;}small{color:#69797b;font-size:14px;font-weight:500;}svg{color:#b45f40;font-size:19px;transition:transform .2s;}&:hover svg{transform:translateX(3px);}@media(max-width:600px){min-height:72px;padding:14px 8px;gap:15px;strong{font-size:17px;}small{font-size:13px;}svg{font-size:20px;}}`;
const MapDot = styled.i`width:9px;height:9px;background:#b45f40;border-radius:50%;box-shadow:0 0 0 5px rgba(180,95,64,.1);`;
const ZoneLoading = styled.div`display:grid;min-height:260px;place-content:center;justify-items:center;gap:14px;color:#748285;font-size:13px;text-align:center;i{width:28px;height:28px;border:2px solid rgba(41,71,79,.12);border-top-color:#b45f40;border-radius:50%;animation:zone-spin .8s linear infinite;}@keyframes zone-spin{to{transform:rotate(360deg);}}`;
const EmptyList = styled.div`display:grid;min-height:290px;place-content:center;justify-items:center;padding:35px 22px;color:#7b898a;text-align:center;strong{max-width:280px;margin:14px 0 8px;color:#29474f;font-size:17px;font-weight:580;line-height:1.35;}span{max-width:275px;font-size:13px;line-height:1.6;}`;
const EmptyMark = styled.i`display:grid;width:42px;height:42px;place-items:center;color:#fff;background:#b45f40;border-radius:50%;font-style:normal;font-size:19px;box-shadow:0 10px 24px rgba(180,95,64,.22);`;
const ContinuePanel = styled.div`display:flex;align-items:flex-end;justify-content:space-between;gap:30px;margin-top:28px;padding:28px 0;border-top:1px solid rgba(42,64,70,.16);border-bottom:1px solid rgba(42,64,70,.16);@media(max-width:650px){align-items:stretch;flex-direction:column;gap:22px;}`;
const ContinueTitle = styled.h2`margin:0;color:#20373c;font-size:clamp(28px,3vw,44px);font-weight:540;letter-spacing:-.045em;`;
const ContinueCopy = styled.p`margin:7px 0 0;color:#738083;font-size:14px;`;
const ContinueButton = styled.button`display:inline-flex;min-height:50px;align-items:center;justify-content:center;gap:10px;padding:0 24px;color:#fff;background:#29474f;border:0;border-radius:999px;font:inherit;font-size:13px;font-weight:680;cursor:pointer;transition:.22s;&:hover{background:#b45f40;transform:translateY(-2px);}svg{font-size:17px;}@media(max-width:650px){width:min(100%,270px);min-height:46px;align-self:flex-start;}`;
